import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useResource, formatDate } from "../../lib/hooks";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../lib/branding";

/**
 * C2: the no-code report builder. Pick a dataset, tick columns, add filters
 * and a date range, run - a table appears, CSV one click away. A saved
 * report stores the SELECTION (dataset + config), never the data, and the
 * shared ones are the house templates.
 *
 * Every run happens server-side under the caller's own RLS and permissions;
 * this page only ever shows what the API let through.
 */

interface DatasetColumnMeta {
    key: string;
    label: string;
    type: "text" | "date" | "datetime" | "number" | "boolean";
}

interface DatasetMeta {
    key: string;
    label: string;
    hasDateRange: boolean;
    columns: DatasetColumnMeta[];
}

interface Filter {
    key: string;
    op: "eq" | "neq" | "contains" | "gte" | "lte";
    value: string;
}

interface ReportResult {
    columns: DatasetColumnMeta[];
    rows: Array<Record<string, unknown>>;
}

interface SavedReport {
    id: string;
    name: string;
    dataset: string;
    config: {
        columns: string[];
        filters?: Filter[];
        groupBy?: string;
        from?: string;
        to?: string;
    };
    shared: boolean;
    created_by: string | null;
}

const OPS: Array<{ value: Filter["op"]; label: string }> = [
    { value: "eq", label: "is" },
    { value: "neq", label: "is not" },
    { value: "contains", label: "contains" },
    { value: "gte", label: "at least" },
    { value: "lte", label: "at most" },
];

const cell = (value: unknown, type: DatasetColumnMeta["type"]): string => {
    if (value === null || value === undefined) return "—";
    if (type === "boolean") return value === true ? "Yes" : "No";
    if ((type === "date" || type === "datetime") && typeof value === "string") return formatDate(value);
    return String(value);
};

/** Client-side CSV of what is already on screen - the server sent it, the browser saves it. */
const downloadCsv = (result: ReportResult, name: string): void => {
    const esc = (v: string): string => (/[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v);
    const lines = [
        result.columns.map((c) => esc(c.label)).join(","),
        ...result.rows.map((r) =>
            result.columns.map((c) => esc(cell(r[c.key], c.type).replace("—", ""))).join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${name.replace(/[^\w-]+/g, "-").toLowerCase() || "report"}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
};

/** Best-effort logo fetch for the PDF header; a missing logo is never an error. */
const logoAsDataUrl = async (url: string | null): Promise<string | null> => {
    if (!url) return null;
    try {
        const blob = await (await fetch(url)).blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
};

/**
 * The same numbers as the CSV, in a document fit for a tender or a board
 * pack: organisation name (and logo when one is set), report title, run
 * date, tidy table, page numbers. jspdf is imported on click so the report
 * builder itself stays light.
 */
const downloadPdf = async (
    result: ReportResult, name: string, orgName: string, logoUrl: string | null
): Promise<void> => {
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;

    const doc = new jsPDF({
        orientation: result.columns.length > 6 ? "landscape" : "portrait",
        unit: "pt", format: "a4",
    });
    const logo = await logoAsDataUrl(logoUrl);
    if (logo) {
        try {
            doc.addImage(logo, "PNG", 40, 28, 36, 36);
        } catch {
            // An unreadable logo never stops the report.
        }
    }
    const textX = logo ? 88 : 40;
    doc.setFontSize(16).setFont("helvetica", "bold").text(orgName, textX, 44);
    doc.setFontSize(12).setFont("helvetica", "normal").text(name, textX, 62);
    doc.setFontSize(9).setTextColor(110).text(
        `Generated ${new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}`
        + ` — ${result.rows.length} row${result.rows.length === 1 ? "" : "s"}`,
        textX, 76);

    autoTable(doc, {
        startY: 92,
        head: [result.columns.map((c) => c.label)],
        body: result.rows.map((r) => result.columns.map((c) => cell(r[c.key], c.type))),
        styles: { fontSize: 8.5, cellPadding: 4 },
        headStyles: { fillColor: [15, 118, 110], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 248, 248] },
        margin: { left: 40, right: 40 },
        didDrawPage: () => {
            const page = doc.internal.pageSize;
            doc.setFontSize(8).setTextColor(150).text(
                `${orgName} — ${name} — page ${doc.getNumberOfPages()}`,
                40, page.getHeight() - 20);
        },
    });

    doc.save(`${name.replace(/[^\w-]+/g, "-").toLowerCase() || "report"}.pdf`);
};

export const ReportBuilder = () => {
    const { mayQuality } = useAuth();
    const branding = useBranding();
    const mayShare = mayQuality("reports.manage");

    const datasets = useResource<DatasetMeta[]>("/report-datasets");
    const saved = useResource<SavedReport[]>("/reports");

    const [datasetKey, setDatasetKey] = useState<string>("");
    const [picked, setPicked] = useState<string[]>([]);
    const [filters, setFilters] = useState<Filter[]>([]);
    const [groupBy, setGroupBy] = useState<string>("");
    const [from, setFrom] = useState<string>("");
    const [to, setTo] = useState<string>("");
    const [result, setResult] = useState<ReportResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [saveName, setSaveName] = useState("");
    const [saveShared, setSaveShared] = useState(false);

    const dataset = useMemo(
        () => (datasets.data ?? []).find((d) => d.key === datasetKey) ?? null,
        [datasets.data, datasetKey]);

    useEffect(() => {
        // A new dataset is a new question: selections reset with it.
        setPicked([]);
        setFilters([]);
        setGroupBy("");
        setResult(null);
    }, [datasetKey]);

    const config = () => ({
        columns: picked,
        ...(filters.length ? { filters: filters.map((f) => ({ ...f, value: f.value })) } : {}),
        ...(groupBy ? { groupBy } : {}),
        ...(from ? { from: new Date(from).toISOString() } : {}),
        ...(to ? { to: new Date(`${to}T23:59:59`).toISOString() } : {}),
    });

    const run = async () => {
        if (!dataset || picked.length === 0) return;
        setBusy(true);
        setError(null);
        try {
            setResult(await api.post<ReportResult>("/report-runs", { dataset: dataset.key, config: config() }));
        } catch (err) {
            setError(err instanceof Error ? err.message : "The report could not run");
        } finally {
            setBusy(false);
        }
    };

    const save = async () => {
        if (!dataset || picked.length === 0 || !saveName.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await api.post("/reports", {
                name: saveName.trim(), dataset: dataset.key, config: config(), shared: saveShared,
            });
            setSaveName("");
            setSaveShared(false);
            saved.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "The report could not be saved");
        } finally {
            setBusy(false);
        }
    };

    const load = (report: SavedReport) => {
        setDatasetKey(report.dataset);
        // The dataset effect clears state; apply the selection after it.
        setTimeout(() => {
            setPicked(report.config.columns);
            setFilters(report.config.filters ?? []);
            setGroupBy(report.config.groupBy ?? "");
        }, 0);
    };

    const removeSaved = async (id: string) => {
        await api.delete(`/reports/${id}`);
        saved.reload();
    };

    return (
        <div style={{ display: "grid", gap: "0.85rem" }}>
            <div className="card">
                <div className="card-head">
                    <div>
                        <h2>Saved reports</h2>
                        <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                            A saved report is a selection, never a copy of the data — running one
                            always shows today&rsquo;s truth, scoped to whoever runs it.
                        </p>
                    </div>
                </div>
                <div className="card-body">
                    <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                        {(saved.data ?? []).map((r) => (
                            <span key={r.id} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                                <button className="icon-btn" onClick={() => load(r)}
                                    title={r.shared ? "Shared with everyone" : "Yours"}>
                                    {r.shared ? "★ " : ""}{r.name}
                                </button>
                                {(!r.shared || mayShare) && r.created_by !== null && (
                                    <button className="icon-btn" aria-label={`Delete ${r.name}`}
                                        onClick={() => void removeSaved(r.id)}>×</button>
                                )}
                            </span>
                        ))}
                        {(saved.data ?? []).length === 0 && <span className="empty">No saved reports yet.</span>}
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-head">
                    <h2>Build a report</h2>
                    <select aria-label="Dataset" value={datasetKey} onChange={(e) => setDatasetKey(e.target.value)}>
                        <option value="">Choose a dataset…</option>
                        {(datasets.data ?? []).map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                    </select>
                </div>
                {dataset && (
                    <div className="card-body" style={{ display: "grid", gap: "0.7rem" }}>
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                            {dataset.columns.map((c) => (
                                <label key={c.key} style={{ display: "inline-flex", gap: 4, alignItems: "center",
                                    fontSize: "0.85rem" }}>
                                    <input type="checkbox" checked={picked.includes(c.key)}
                                        onChange={(e) => setPicked(e.target.checked
                                            ? [...picked, c.key]
                                            : picked.filter((k) => k !== c.key))} />
                                    {c.label}
                                </label>
                            ))}
                        </div>

                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                            {dataset.hasDateRange && (
                                <>
                                    <label style={{ fontSize: "0.8rem" }}>From{" "}
                                        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                                    <label style={{ fontSize: "0.8rem" }}>To{" "}
                                        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
                                </>
                            )}
                            <label style={{ fontSize: "0.8rem" }}>Group by{" "}
                                <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                                    <option value="">— no grouping —</option>
                                    {dataset.columns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                                </select></label>
                            <button className="icon-btn"
                                onClick={() => setFilters([...filters, { key: dataset.columns[0]!.key, op: "eq", value: "" }])}>
                                + Filter
                            </button>
                        </div>

                        {filters.map((f, i) => (
                            <div key={i} style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                                <select aria-label={`Filter ${i + 1} column`} value={f.key}
                                    onChange={(e) => setFilters(filters.map((x, j) => j === i ? { ...x, key: e.target.value } : x))}>
                                    {dataset.columns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                                </select>
                                <select aria-label={`Filter ${i + 1} operator`} value={f.op}
                                    onChange={(e) => setFilters(filters.map((x, j) => j === i ? { ...x, op: e.target.value as Filter["op"] } : x))}>
                                    {OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                                <input aria-label={`Filter ${i + 1} value`} value={f.value}
                                    onChange={(e) => setFilters(filters.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
                                <button className="icon-btn" aria-label={`Remove filter ${i + 1}`}
                                    onClick={() => setFilters(filters.filter((_, j) => j !== i))}>×</button>
                            </div>
                        ))}

                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                            <button className="btn" disabled={busy || picked.length === 0} onClick={() => void run()}>
                                {busy ? "Running…" : "Run report"}
                            </button>
                            <input placeholder="Save as…" value={saveName}
                                onChange={(e) => setSaveName(e.target.value)} style={{ width: 180 }} />
                            {mayShare && (
                                <label style={{ fontSize: "0.8rem", display: "inline-flex", gap: 4, alignItems: "center" }}>
                                    <input type="checkbox" checked={saveShared}
                                        onChange={(e) => setSaveShared(e.target.checked)} />
                                    Share with everyone
                                </label>
                            )}
                            <button className="icon-btn" disabled={busy || picked.length === 0 || !saveName.trim()}
                                onClick={() => void save()}>
                                Save
                            </button>
                            {result && (
                                <>
                                    <button className="icon-btn" onClick={() => downloadCsv(result, saveName || dataset.label)}>
                                        Download CSV
                                    </button>
                                    <button className="icon-btn"
                                        onClick={() => void downloadPdf(result, saveName || dataset.label,
                                            branding.orgName, branding.logoUrl)}>
                                        Download PDF
                                    </button>
                                </>
                            )}
                        </div>
                        {error && <div className="error-banner">{error}</div>}
                    </div>
                )}
            </div>

            {result && (
                <div className="card">
                    <div className="card-head">
                        <h2>{result.rows.length} row{result.rows.length === 1 ? "" : "s"}</h2>
                    </div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead><tr>
                                    {result.columns.map((c) => <th key={c.key}>{c.label}</th>)}
                                </tr></thead>
                                <tbody>
                                    {result.rows.map((r, i) => (
                                        <tr key={i}>
                                            {result.columns.map((c) => (
                                                <td key={c.key} className={c.type === "number" ? "mono" : undefined}>
                                                    {cell(r[c.key], c.type)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    {result.rows.length === 0 && (
                                        <tr><td colSpan={result.columns.length} className="empty">
                                            Nothing matches. Loosen a filter or widen the dates.
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

